// Attaches the Cognito access token to backend calls that do not go through
// apiConfig (raw fetch() and the default axios instance). The backend rejects
// every /api/ and /auth/ request without a valid token, and many pages still
// build their own header from localStorage.getItem("token"), which is never
// set. New code should use apiConfig instead of relying on this.
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PROTECTED_PATH = /^\/(api|auth)\//;

const backendOrigin = (() => {
  try {
    return new URL(BACKEND_URL || '/', window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
})();

// Only our own backend ever receives the token - never S3, Cognito or any
// other host a page happens to call.
export const isBackendUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    const isOurHost =
      parsed.origin === window.location.origin || parsed.origin === backendOrigin;
    return isOurHost && PROTECTED_PATH.test(parsed.pathname);
  } catch {
    return false;
  }
};

// "Bearer null" / "Bearer undefined" / "Bearer " are what the legacy call
// sites send, so they count as no token at all.
export const hasBearerToken = (value) =>
  typeof value === 'string' &&
  /^Bearer\s+(?!null$|undefined$)\S+$/i.test(value.trim());

export const getAccessToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session?.tokens?.accessToken?.toString() || null;
  } catch {
    return null;
  }
};

let installed = false;

export const installBackendAuth = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use(async (config) => {
    let url;
    try {
      url = new URL(config.url, config.baseURL || window.location.origin).href;
    } catch {
      return config;
    }
    if (!isBackendUrl(url)) return config;
    if (hasBearerToken(config.headers?.get?.('Authorization'))) return config;

    const token = await getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  });

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const isRequest = typeof Request !== 'undefined' && input instanceof Request;
    const url = isRequest ? input.url : String(input);
    if (!isBackendUrl(url)) return nativeFetch(input, init);

    const headers = new Headers(init?.headers || (isRequest ? input.headers : undefined));
    if (hasBearerToken(headers.get('Authorization'))) return nativeFetch(input, init);

    const token = await getAccessToken();
    if (!token) return nativeFetch(input, init);

    headers.set('Authorization', `Bearer ${token}`);
    return nativeFetch(input, { ...init, headers });
  };
};
